howspace.components.textEditor = (() => {
    'use strict';

    let toolbars = {};
    let tagsPluginInitialized = false;

    // The iframe cannot use the page stylesheet. Keep table geometry in sync with widgets/textbox.css.
    const simpleTableStyles = `
        .howspace-simple-tables { min-width: 0; overflow-x: auto; }
        .howspace-simple-tables table {
            border-collapse: collapse;
            border-spacing: 0;
            width: 100%;
            margin-block: var(--howspace-size-3);
        }
        .mce-content-body.howspace-simple-tables table :is(td, th) {
            border: 1px solid var(--howspace-color-table-border);
            padding: var(--howspace-size-2);
            min-width: 6rem;
            height: calc(1lh + 2 * var(--howspace-size-2) + 1px);
            box-sizing: border-box;
            vertical-align: top;
            text-align: start;
            word-break: normal;
            overflow-wrap: normal;
        }
        .mce-content-body.howspace-simple-tables table th { font-weight: 700; }
        .howspace-simple-tables :is(td, th) > :first-child { margin-top: 0; }
        .howspace-simple-tables :is(td, th) > :last-child { margin-bottom: 0; }
    `;

    const plugins = [
        'code',
        'autoresize',
        'autolink',
        'emoticons',
        'link',
        'lists',
        'directionality',
        'howspacetags'
    ];

    async function waitForTinyMCE() {
        await lib('tinymce', 'tinymce.min.js');

        if (!tagsPluginInitialized) {
            tinymce.PluginManager.add('howspacetags', function (editor) {
                editor.ui.registry.addMenuItem('userFirstName', {
                    text: howspace.translate('common_ckeditor_tags_TO_USER_FIRST_NAME'),
                    onAction: () => {
                        editor.insertContent('[TO_USER_FIRST_NAME]');
                    }
                });

                editor.ui.registry.addMenuItem('userDisplayName', {
                    text: howspace.translate('common_ckeditor_tags_TO_USER_DISPLAY_NAME'),
                    onAction: () => {
                        editor.insertContent('[TO_USER_DISPLAY_NAME]');
                    }
                });

                editor.ui.registry.addMenuItem('workspaceName', {
                    text: howspace.translate('common_ckeditor_tags_WORKSPACE_NAME'),
                    onAction: () => {
                        editor.insertContent('[WORKSPACE_NAME]');
                    }
                });

                editor.ui.registry.addMenuButton('howspacetags', {
                    text: howspace.translate('common_ckeditor_tags'),
                    tooltip: howspace.translate('common_ckeditor_tags'),
                    fetch: callback => {
                        const items = ['userFirstName', 'userDisplayName'];
                        // Only offered where something will substitute it. A customer level email is not sent
                        // from a workspace, so the tag would go out to the recipient as written.
                        if (editor.targetElm?.dataset.withoutWorkspaceTag !== 'true') {
                            items.push('workspaceName');
                        }
                        callback(items.join(' '));
                    }
                });

                return {
                    getMetadata: () => {
                        return {
                            name: 'Howspace Tags Plugin',
                            url: 'https://howspace.com'
                        };
                    }
                };
            });

            // https://www.tiny.cloud/docs/tinymce/6/available-toolbar-buttons/
            toolbars = {
                'generic': `
                    bold italic strikethrough |
                    undo redo |
                    styles blocks |
                    bullist numlist hr |
                    howspacetags |
                    link unlink |
                    emoticons |
                    alignleft aligncenter alignright
                `,
                'workspace-description': `
                    bold italic |
                    undo redo |
                    bullist numlist |
                    link unlink |
                    blocks |
                    emoticons
                `,
                'email': `
                    bold italic |
                    undo redo |
                    bullist numlist |
                    howspacetags |
                    link unlink |
                    blocks |
                    emoticons
                `,
                'customer-email': `
                    bold italic |
                    undo redo |
                    bullist numlist |
                    howspacetags |
                    blocks |
                    emoticons
                `,
                'simple': `
                    bold italic |
                    undo redo |
                    bullist numlist |
                    howspacetags
                `,
                'simple-links': `
                    bold italic strikethrough |
                    undo redo |
                    bullist numlist |
                    link unlink
                `
            };

            // If support for the text direction is enabled, add the 'directionality' plugin to the list of plugins.
            // and add the 'ltr rtl' buttons to the toolbar generic and workspace-description.
            if (textDirectionSupport) {
                if (toolbars['generic'].indexOf('ltr rtl') === -1) {
                    toolbars['generic'] += ' | ltr rtl';
                }
                if (toolbars['workspace-description'].indexOf('ltr rtl') === -1) {
                    toolbars['workspace-description'] += ' | ltr rtl';
                }
                if (toolbars['email'].indexOf('ltr rtl') === -1) {
                    toolbars['email'] += ' | ltr rtl';
                }
            }

            tagsPluginInitialized = true;
        }
    }

    const repaintEditor = editor => {
        if (!editor) {
            return;
        }
        // Dispatch the 'SkinLoaded' event to force a repaint of the editor UI.
        editor.dispatch('SkinLoaded');
    };

    let textDirectionSupport = false;

    // TinyMCE renders its UI and its content iframe outside our stylesheets, so it cannot follow the design
    // tokens. It needs its own skin instead. Read the resolved theme from --howspace-color-scheme rather than
    // matchMedia, because only the apps that bundle howspace-dark.css actually turn dark.
    //
    // Read off the editor rather than off the document, because the token inherits and a light island in a
    // dark page puts it back. The email preview is one: it is a picture of an email, which is a white page
    // wherever it is read, so an editor inside it is light even while the page around it is dark.
    const isDarkMode = (elem) => {
        return howspace.getCssVariable(elem, '--howspace-color-scheme').trim() === 'dark';
    };

    return {
        INITIALIZED: 'howspace-text-editor-initialized',
        CONTENT_CHANGED: 'howspace-text-editor-content-changed',
        init: (elem) => {
            elem.findIncludingSelf('.howspace-text-editor:not(.howspace-text-editor-initialized)').each(async function () {
                const editorTextarea = $(this);

                if (!editorTextarea.attr('id')) {
                    editorTextarea.attr('id', 'howspace-text-editor-' + real.uid());
                }

                await waitForTinyMCE();

                const editorType = editorTextarea.data('toolbar') ? editorTextarea.data('toolbar') : 'generic';
                const toolbar = toolbars[editorType] ? toolbars[editorType] : toolbars['generic'];
                const simpleTables = editorTextarea.data('simple-tables') === true;

                const wrapper = editorTextarea.closest('.howspace-text-editor-wrapper');
                const darkMode = isDarkMode(editorTextarea);

                // Autoresize sizes an empty editor to a single line, which leaves it shorter than the height
                // the wrapper reserves and clips a placeholder that wraps onto a second line. Floor the editor
                // at the reserved height so it fills the wrapper, reading the height off the wrapper to keep
                // text-editor.css the single source of truth.
                const reservedHeight = wrapper.height();

                let language = real.dom.$html.attr('lang');
                switch (language) {
                    case 'de-formal':
                        language = 'de';
                        break;
                    case 'es-419':
                        language = 'es';
                        break;
                    case 'fr':
                        language = 'fr_FR';
                        break;
                    case 'nb':
                        language = 'nb_NO';
                        break;
                    case 'sv':
                        language = 'sv_SE';
                        break;
                }

                tinymce.init({
                    language: language,
                    target: editorTextarea[0],
                    menubar: false,
                    plugins: simpleTables ? [...plugins, 'table'] : plugins,
                    contextmenu: false,
                    link_list: (success) => {
                        real.ajax.json(real.baseUri + '/api/links', {
                            type: 'GET',
                            success: (response) => {
                                let links = [];
                                response.forEach(item => {
                                    links.push({
                                        title: item.title,
                                        value: '/' + item.path
                                    });
                                });
                                success(links);
                            }
                        });
                    },
                    relative_urls: false,
                    remove_script_host: false,
                    document_base_url: real.dom.$html.data('base-uri'),
                    text_patterns: [
                        {start: '#', format: 'h2'},
                        {start: '##', format: 'h3'},
                        {start: '1.', cmd: 'InsertOrderedList'},
                        {start: '-', cmd: 'InsertUnorderedList'}
                    ],
                    autoresize_bottom_margin: 1,
                    ...(reservedHeight > 0 ? {min_height: reservedHeight} : {}),
                    branding: false,
                    statusbar: false,
                    license_key: 'gpl',
                    skin: darkMode ? 'howspace-dark' : 'oxide',
                    content_css: darkMode ? 'howspace-dark' : 'default',
                    toolbar: simpleTables ? toolbar + ' | simpletable' : toolbar,
                    ...(simpleTables ? {
                        body_class: 'howspace-simple-tables',
                        table_toolbar: 'tabledelete | tablerowheader tablecolheader | tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                            'tableinsertcolbefore tableinsertcolafter tabledeletecol',
                        table_grid: true,
                        table_header_type: 'sectionCells',
                        table_resize_bars: false,
                        object_resizing: false,
                        table_sizing_mode: 'responsive',
                        table_use_colgroups: false,
                        table_default_attributes: {},
                        table_default_styles: {},
                        table_advtab: false,
                        table_row_advtab: false,
                        table_cell_advtab: false,
                        table_appearance_options: false,
                        extended_valid_elements: 'table[dir|lang],thead[dir|lang],tbody[dir|lang],tfoot[dir|lang],' +
                            'tr[dir|lang],td[dir|lang|style],th[dir|lang|style|scope]'
                    } : {}),
                    toolbar_mode: 'sliding',
                    link_default_target: '_blank',
                    link_title: false,
                    link_assume_external_targets: 'https',
                    link_default_protocol: 'https',
                    link_class_list: editorType !== 'email' ? [
                        {title: howspace.translate('common_none'), value: ''},
                        {title: howspace.translate('common_primary_button'), value: 'btn btn-primary'},
                        {title: howspace.translate('common_secondary_button'), value: 'btn btn-secondary'}
                    ] : [],
                    block_formats: howspace.translate('common_paragraph') + '=p; ' + howspace.translate('common_heading') + '=h2; ' + howspace.translate('common_subheading') + '=h3;',
                    valid_styles: {
                        '*': 'text-align'
                    },
                    style_formats: [
                        {
                            name: 'highlight',
                            title: howspace.translate('common_highlight_text'),
                            inline: 'span',
                            classes: ['highlight-text']
                        },
                        {
                            name: 'highlight-bg',
                            title: howspace.translate('common_highlight_background'),
                            inline: 'span',
                            classes: ['highlight-text-bg']
                        }
                    ],
                    paste_data_images: false,
                    paste_preprocess: (plugin, args) => {
                        // Remove images from the pasted content.
                        args.content = args.content.replace(/<img[^>]+>/g, '');
                    },
                    highlight_on_focus: false,
                    content_style: "" +
                        ".mce-content-body { min-height: 3em; }" +
                        ".mce-content-body a.btn { border-radius: 1.3rem; padding: .6875rem 1rem; background: hsl(257, 59%, 55%); color: white; border: 1px solid hsl(257, 59%, 55%); text-decoration: none; display: inline-block; }" +
                        ".mce-content-body a.btn.btn-secondary { background: white; color: hsl(257, 59%, 55%); }" +
                        ".mce-content-body span { color: inherit !important; font: inherit !important; text-decoration: inherit !important; }" +
                        ".mce-content-body .highlight-text { background-color: white; color: hsl(257, 59%, 55%) !important; }" +
                        ".mce-content-body .highlight-text-bg { background-color: hsl(257, 59%, 55%); color: white !important; padding-left: 0.2em; padding-right: 0.2em; }" +
                        (simpleTables ? simpleTableStyles : ''),
                    setup: editor => {
                        if (simpleTables) {
                            const announceTableChange = () => editor.announce(
                                howspace.translate('common_text_editor_table_updated')
                            );
                            editor.on('TableModified', event => {
                                if (event.structure) announceTableChange();
                            });
                            editor.on('ExecCommand', event => {
                                if (event.command === 'mceInsertTable' || event.command === 'mceTableDelete') {
                                    announceTableChange();
                                }
                            });
                            editor.ui.registry.addMenuButton('simpletable', {
                                icon: 'table',
                                tooltip: 'Table',
                                fetch: callback => callback('inserttable | tablerowheader tablecolheader | ' +
                                    'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                                    'tableinsertcolumnbefore tableinsertcolumnafter tabledeletecolumn | deletetable')
                            });
                            editor.on('init', () => {
                                for (const token of ['--howspace-color-table-border', '--howspace-size-2', '--howspace-size-3']) {
                                    editor.getDoc().documentElement.style.setProperty(
                                        token, howspace.getCssVariable(editorTextarea, token).trim()
                                    );
                                }
                            });
                        }
                        editor.on('init', () => {
                            // The directionality plugin checks only the immediate block for a
                            // 'dir' attribute. For list items the block is <li>, which never carries
                            // 'dir' — only the parent <ol>/<ul> does. Patching dom.getAttrib makes
                            // <li> elements transparently inherit 'dir' from their parent list so
                            // the RTL toolbar button reflects the correct state.
                            const dom = editor.dom;
                            const origGetAttrib = dom.getAttrib.bind(dom);
                            dom.getAttrib = (elm, name, defaultVal) => {
                                if (name === 'dir' && elm?.nodeName?.toLowerCase() === 'li') {
                                    const val = origGetAttrib(elm, name, defaultVal);
                                    if (!val) {
                                        const list = dom.getParent(elm, 'ol,ul');
                                        if (list) return origGetAttrib(list, name, defaultVal);
                                    }
                                    return val;
                                }
                                return origGetAttrib(elm, name, defaultVal);
                            };

                            // Browsers auto-add dir="rtl" to <li> when RTL text is typed
                            // (bidi detection). The server sanitizer only allows dir on <ul>/<ol>,
                            // so dir on <li> would be stripped. Move it up to the parent list.
                            editor.serializer.addNodeFilter('li', nodes => {
                                nodes.forEach(node => {
                                    const dir = node.attr('dir');
                                    if (dir) {
                                        const parent = node.parent;
                                        if (parent?.name === 'ul' || parent?.name === 'ol') {
                                            if (!parent.attr('dir')) {
                                                parent.attr('dir', dir);
                                            }
                                            // Only remove from <li> when the parent now carries the same
                                            // value — either we just set it, or it was already redundant.
                                            // If the parent has a conflicting dir, keep <li>'s dir intact.
                                            if (parent.attr('dir') === dir) {
                                                node.attr('dir', null);
                                            }
                                        }
                                    }
                                });
                            });
                        });

                        editor.on('init', () => {
                            // TinyMCE will steal focus and scroll page on init. Let's fix this manually.
                            const origActiveElement = document.activeElement;
                            const origScrollY = document.documentElement.scrollTop;
                            const origScrollX = document.documentElement.scrollLeft;

                            setTimeout(() => {
                                const loader = wrapper.find('.howspace-loader');
                                const editorUI = wrapper.find('.tox-tinymce');
                                const origHeight = wrapper.height();

                                wrapper.removeClass('initializing');
                                repaintEditor(editor);

                                // Remove focus immediately from the editor and restore the scroll position.
                                document.activeElement.blur();
                                origActiveElement?.focus();
                                document.documentElement.scrollTop = origScrollY;
                                document.documentElement.scrollLeft = origScrollX;

                                requestAnimationFrame(() => {
                                    const animHeight = wrapper.height();

                                    howspace.animate(loader, 'fadeOut', 350, () => {
                                        loader.remove();
                                    });

                                    wrapper.css('overflow', 'hidden');
                                    wrapper.height(origHeight);
                                    wrapper.velocity({height: animHeight}, {
                                        duration: 350,
                                        complete: () => {
                                            wrapper.css({height: '', overflow: ''});

                                            // If the editor is inside a modal, move the TinyMCE aux element to the modal.
                                            // this prevents the aux element from being hidden behind the modal.
                                            if (editorTextarea.closest('.modal').length > 0) {
                                                const modal = editorTextarea.closest('.modal');
                                                modal.append($('body .tox-tinymce-aux'));
                                            }

                                            howspace.dispatchEvent(editor.targetElm, howspace.components.textEditor.INITIALIZED);
                                        }
                                    });
                                });
                            }, 100);
                        });

                        const contentChanged = () => {
                            howspace.activityTracker.resetIdleTimeout();
                            const content = editor.getContent();
                            const isEmpty = content === '';
                            howspace.dispatchEvent(editor.targetElm, howspace.components.textEditor.CONTENT_CHANGED, {
                                content: content,
                                isEmpty: isEmpty
                            });
                        };

                        editor.on('input', contentChanged);
                        editor.on('change', contentChanged);

                        editorTextarea.closest('label').on('click', () => {
                            if (editor.initialized) {
                                editor.focus();
                            }
                        });

                        editor.on('focus', () => {
                            wrapper.addClass('focused');
                        });

                        editor.on('blur', () => {
                            wrapper.removeClass('focused');
                        });

                        editor.on('BeforeSetContent', function (event) {
                            // Encode the <img> tag as a string to prevent TinyMCE from converting it into an actual image
                            event.content = event.content.replace(/<img([^>]*)>/g, '&lt;img$1&gt;');
                        });
                    }
                });

                editorTextarea.addClass('howspace-text-editor-initialized');
            });
        },
        destroy: (elem) => {
            elem.findIncludingSelf('.howspace-text-editor.howspace-text-editor-initialized').each(async function () {
                await waitForTinyMCE();
                const editorTextarea = $(this);
                const wrapper = editorTextarea.closest('.howspace-text-editor-wrapper');
                tinymce.remove('#' + editorTextarea.attr('id'));
                // Remove initialized class after (!) removing TinyMCE editor because the TinyMCE remove
                // restores the textarea classes to what they were before initializing the editor.
                editorTextarea.removeClass('howspace-text-editor-initialized');
                editorTextarea.removeAttr('id');
                wrapper.removeClass('initializing focused');
            });
        },
        getContent: (elem) => {
            const editor = window.tinymce?.get(elem.attr('id'));
            if (!editor?.initialized) {
                return elem.val();
            }

            return editor.getContent();
        },
        focus: (elem) => {
            if (!window.tinymce) {
                return;
            }

            const editor = tinymce.get(elem.attr('id'));
            if (editor?.initialized) {
                editor.focus();
            }
        },
        repaint: (elem) => {
            elem.findIncludingSelf('.howspace-text-editor.howspace-text-editor-initialized').each(async function () {
                await waitForTinyMCE();
                repaintEditor(tinymce.get(this.id));
            });
        },
        enableTextDirectionSupport: () => {
            if (textDirectionSupport) {
                return;
            }
            textDirectionSupport = true;
        },
        waitForTinyMCE: waitForTinyMCE,
    };

})();
